import * as lodash from 'lodash'

// QuickBooks
export interface QbAccountResponse {
  id: string // QuickBooks account ID
  name: string // Account name
  accountType: string // Account type (e.g., Asset, Liability, Equity, Income, Expense)
  accountSubType: string // Account subtype
  classification: string // Account classification
  active: boolean // Whether the account is active
  currentBalance: number // Current balance of the account
  description: string // Account description
}

export interface QbAutoExport {
  onCreate: boolean // Auto-export on creation
  onUpdate: boolean // Auto-export on update
  onDelete: boolean // Auto-export on deletion
}

export interface QbSettings {
  expenseSettings: {
    lineCategoryAccountId: string // QuickBooks line category account ID
    paymentAccountId: string // QuickBooks payment account ID
    checkPaymentAccountId: string // QuickBooks check payment account ID
    autoExport: QbAutoExport
  }
  chargeSettings: {
    depositAccountId: string // QuickBooks deposit account ID for charges
    autoExport: QbAutoExport
  }
  extraSettings: {
    depositAccountId: string // QuickBooks deposit account ID for extras
    autoExport: QbAutoExport
  }
  invoiceSettings: {
    depositAccountId: string // QuickBooks deposit account ID for invoices
    autoExport: QbAutoExport
  }
  guestSettings: {
    duplicateNameResolver: 'override' | 'create_with_unique_suffix' // How to handle duplicate guest names
    autoExport: QbAutoExport
  }
}

export interface QbStatusResponse extends QbSettings {
  connected: boolean // Whether the account is connected to QuickBooks
  realmId: string // QuickBooks realm ID
}

export interface QbExportStats {
  total: {
    guests: number // Total guests synced
    invoices: number // Total invoices synced
    expenses: number // Total expenses synced
    charges: number // Total charges synced
    extras: number // Total extras synced
  }
  thisWeek: {
    guests: number // Total guests synced
    invoices: number // Total invoices synced
    expenses: number // Total expenses synced
    charges: number // Total charges synced
    extras: number // Total extras synced
  }
}

export interface QbDashboardResponse extends QbStatusResponse {
  companyName: string // QuickBooks company name
  exportStats: QbExportStats
  lastSync: string // Last sync date
}

export interface QbTestConnectionResponse {
  success: boolean // Whether the QuickBooks connection test was successful

  jobId: string // Job ID for tracking the test activity

  companyName: string // QuickBooks company name retrieved from API

  realmId: string // QuickBooks realm ID

  error: string // Error message if the test failed
}

export interface QbExportObjectPayload {
  objectType?: 'expense' | 'guest' | 'invoice' | 'charge' | 'extra' // The type of Tokeet object to export
  pkeys?: string[] // Optional array of specific primary keys (pkeys) to export. If not provided, all objects of the specified type will be synced.
  exportAll?: boolean // If true, all objects of the specified type will be synced.
}
export interface QbActivityLogRequest {
  page?: number // start from 1
  limit?: number
  activityType?: QbActivityType
  status?: QbActivityStatus
}

export enum QbActivityType {
  ConnectionTest = 'connection_test',
  ExportSingleObject = 'export_single_object',
  ExportBatchObjects = 'export_batch_objects',
  ExportAllTypes = 'export_all_types',
  DeleteSingleObject = 'delete_single_object',
  DeleteBatchObjects = 'delete_bath_objects',
}

export enum QbActivityStatus {
  InProgress = 'in_progress',
  Completed = 'completed',
  Failed = 'failed',
}

export interface QbActivityLogItem {
  _id: string
  jobId: string
  account: number
  activityType: QbActivityType
  status: QbActivityStatus
  errorMessage: string
  completedAt: string
  createdAt: string
  updatedAt: string
  metadata?: {
    types: string[]
    count: number
    failed: { error: string; pkey: string }[]
  }
}

export interface QbActivityLogViewItem extends QbActivityLogItem {
  description: string
  title: string
  color: string
}

export interface QbActivityLogsResponse {
  data: QbActivityLogItem[]
  total: number
  page: number
  limit: number
  totalPages: number
}

export function getQbActivityLogStatusColor(status: QbActivityStatus) {
  if (status === QbActivityStatus.Completed) {
    return 'success'
  } else if (status === QbActivityStatus.Failed) {
    return 'danger'
  } else if (status === QbActivityStatus.InProgress) {
    return 'warning'
  }
  return 'secondary'
}

export function getQbActivityLogStatus(status: QbActivityStatus) {
  if (status === QbActivityStatus.Completed) {
    return $localize`Success`
  } else if (status === QbActivityStatus.Failed) {
    return $localize`Failed`
  } else if (status === QbActivityStatus.InProgress) {
    return $localize`Pending`
  }
  return $localize`Unknown`
}

export function getQbActivityLogTitle(item: QbActivityLogItem) {
  const status = getQbActivityLogStatus(item.status)
  const type = lodash.get(item, 'metadata.types.0')
  switch (item.activityType) {
    case QbActivityType.ConnectionTest:
      return $localize`Connection Test` + ` ${status}`
    case QbActivityType.ExportSingleObject:
    case QbActivityType.ExportBatchObjects:
      return $localize`Sync` + ` ${lodash.capitalize(type)} ${status}`
    case QbActivityType.ExportAllTypes:
      return $localize`Sync All Data` + ` ${status}`
    case QbActivityType.DeleteBatchObjects:
    case QbActivityType.DeleteSingleObject:
      return $localize`Deleted` + ` ${lodash.capitalize(type)} ${status}`
    default:
      return $localize`Unknown Activity` + ` ${status}`
  }
}

export function getQbActivityLogDescription(item: QbActivityLogItem) {
  const type = lodash.get(item, 'metadata.types.0')
  const count = lodash.get(item, 'metadata.count')
  switch (item.activityType) {
    case QbActivityType.ConnectionTest:
      if (item.status === QbActivityStatus.Completed) {
        return $localize`AdvanceCM is connected to QuickBooks`
      } else if (item.status === QbActivityStatus.Failed) {
        return $localize`Unable to verify QuickBooks connection, please check your settings.`
      } else if (item.status === QbActivityStatus.InProgress) {
        return $localize`Verifying QuickBooks connection...`
      }
      return ''
    case QbActivityType.ExportSingleObject:
    case QbActivityType.ExportBatchObjects:
      if (item.status === QbActivityStatus.Completed) {
        return `Synced ${count} ${type}(s) to QuickBooks`
      } else if (item.status === QbActivityStatus.Failed) {
        return item.errorMessage || `Failed to sync ${count} ${type}s to QuickBooks`
      } else if (item.status === QbActivityStatus.InProgress) {
        return `Syncing ${count} ${type}(s) from AdvanceCM to QuickBooks`
      }
      return ''
    case QbActivityType.ExportAllTypes:
      if (item.status === QbActivityStatus.Completed) {
        return `Synced ${count} record(s) from AdvanceCM to QuickBooks`
      } else if (item.status === QbActivityStatus.Failed) {
        return item.errorMessage || `Failed to sync all data from AdvanceCM to QuickBooks`
      } else if (item.status === QbActivityStatus.InProgress) {
        return `Syncing ${count} record(s) from AdvanceCM to QuickBooks`
      }
      return ''
    case QbActivityType.DeleteBatchObjects:
    case QbActivityType.DeleteSingleObject:
      if (item.status === QbActivityStatus.Completed) {
        return `Deleted ${count} ${type}(s) from QuickBooks`
      } else if (item.status === QbActivityStatus.Failed) {
        return item.errorMessage || `Failed to delete ${count} ${type}s from QuickBooks`
      } else if (item.status === QbActivityStatus.InProgress) {
        return `Deleting ${count} ${type}(s) from QuickBooks`
      }
      return ''
    default:
      return ''
  }
}
